import { useEffect, useState } from "react";
import axios from "axios";
import { Gift, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const token = () => localStorage.getItem("mahra_session_token");

// Checkout: apply Mahra Rental Credit to rental fees only. Signed-in customers see their credits; anyone can type a number.
export function RentalCreditBox({ bookingIds, applied, onApplied }) {
  const [mine, setMine] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token()) return;
    axios.get(`${API}/rental-credits/mine`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => setMine(r.data.credits.filter((c) => ["active", "partially_used"].includes(c.status) && c.balance > 0)))
      .catch(() => setMine([]));
  }, []);

  const apply = async (number) => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/rental-credits/checkout-preview`, { booking_ids: bookingIds, credit: number.trim() });
      if (r.data.applied <= 0) setError("There's no rental fee left for this credit to pay.");
      else onApplied(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Couldn't apply this credit"); } finally { setBusy(false); }
  };

  if (applied) {
    return (
      <div className="flex items-center justify-between bg-[#9A7B4F]/[0.06] border border-[#9A7B4F]/25 p-4 mb-4 text-[13px]" data-testid="credit-applied-box">
        <div>
          <div className="text-[#282828] font-medium">Mahra Rental Credit {applied.credit_number}</div>
          <div className="text-[11px] text-[#676664]/70">−{fmt(applied.applied)} off your rental · {fmt(applied.balance_after)} left after this order</div>
        </div>
        <button onClick={() => onApplied(null)} className="text-[#676664]/60 hover:text-[#282828]" aria-label="Remove credit" data-testid="credit-remove-btn"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#282828]/8 p-4 mb-4 space-y-3" data-testid="rental-credit-box">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-[#676664]"><Gift className="w-4 h-4 text-[#9A7B4F]" /> Mahra Rental Credit</div>
      {mine.map((c) => (
        <button key={c.number} onClick={() => apply(c.number)} disabled={busy} className="w-full flex justify-between items-center border border-[#9A7B4F]/30 px-3 py-2 text-[12px] hover:bg-[#9A7B4F]/5 transition-colors" data-testid={`my-credit-${c.number}`}>
          <span className="font-mono">{c.number}</span><span className="text-[#9A7B4F] font-medium">{fmt(c.balance)} available · Apply</span>
        </button>
      ))}
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Credit number e.g. MRC-2026-0001" className="h-10 text-[12px]" data-testid="credit-code-input" />
        <Button onClick={() => apply(code)} disabled={busy || !code.trim()} variant="outline" className="h-10 text-[11px] uppercase tracking-wider" data-testid="credit-apply-btn">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
        </Button>
      </div>
      {error && <div className="text-[11px] text-red-600" data-testid="credit-error">{error}</div>}
      <div className="text-[10px] text-[#676664]/50">Credit pays rental fees only — not the refundable bond or delivery.</div>
    </div>
  );
}
